import React, { useState, useEffect, useRef } from 'react';
import { Search, Zap, ArrowRight, Compass, ThumbsUp, ThumbsDown, Shield, TrendingUp, Users, MapPin, Briefcase, GraduationCap, Radio, Filter, X } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SearchTab({ user, onProfileClick }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [sections, setSections] = useState({});
  const [autocomplete, setAutocomplete] = useState([]);
  const [discovery, setDiscovery] = useState({ suggestions: [], layers: {} });
  const [loading, setLoading] = useState(false);
  const [intentLoading, setIntentLoading] = useState(false);
  const [activeMode, setActiveMode] = useState('search');
  const [groupEvents, setGroupEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({ is_live: false, skill: '', city: '', company: '', position: '', college: '' });
  const [trendingSkills, setTrendingSkills] = useState([]);
  const [newUsers, setNewUsers] = useState([]);
  const token = localStorage.getItem('token');
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  useEffect(() => { fetchDiscovery(); }, []);

  const fetchDiscovery = async () => {
    try {
      const res = await fetch(`${API_URL}/api/discover`, { headers });
      if (res.ok) setDiscovery(await res.json());
    } catch {}
  };

  const handleSearch = async (q) => {
    setQuery(q);
    if (!q.trim() && !Object.values(filters).some(v => v)) { setResults([]); setAutocomplete([]); setSections({}); return; }
    // Autocomplete
    if (q.trim()) {
      try {
        const acRes = await fetch(`${API_URL}/api/search/autocomplete?q=${encodeURIComponent(q)}`);
        if (acRes.ok) { const d = await acRes.json(); setAutocomplete(d.suggestions || []); }
      } catch {}
    }
    // Build filter params
    const params = new URLSearchParams({ q: q || '', limit: '20' });
    if (filters.is_live) params.set('is_live', 'true');
    if (filters.skill) params.set('skill', filters.skill);
    if (filters.city) params.set('city', filters.city);
    if (filters.company) params.set('company', filters.company);
    if (filters.position) params.set('position', filters.position);
    if (filters.college) params.set('college', filters.college);
    // Search
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/search?${params.toString()}`);
      if (res.ok) {
        const d = await res.json();
        setResults(d.results || []);
        setSections(d.sections || {});
        if (d.discovery) {
          setTrendingSkills(d.discovery.trending_skills || []);
          setNewUsers(d.discovery.new_users || []);
        }
      }
    } catch {}
    setLoading(false);
  };

  // Re-run search when filters change
  const applyFilters = () => handleSearch(query);
  const clearFilters = () => { setFilters({ is_live: false, skill: '', city: '', company: '', position: '', college: '' }); handleSearch(query); };
  const activeFilterCount = Object.values(filters).filter(v => v).length;

  const handleIntentSearch = async () => {
    if (!query.trim()) return;
    setIntentLoading(true);
    setActiveMode('intent');
    setAutocomplete([]);
    try {
      const res = await fetch(`${API_URL}/api/search/intent?problem=${encodeURIComponent(query)}`, { method: 'POST', headers });
      if (res.ok) { const d = await res.json(); setResults(d.results || []); }
    } catch {}
    setIntentLoading(false);
  };

  const handleFeedback = async (resultUserId, helpful) => {
    try {
      await fetch(`${API_URL}/api/search/feedback?result_user_id=${resultUserId}&query=${encodeURIComponent(query)}&helpful=${helpful}`, { method: 'POST', headers });
    } catch {}
  };

  const selectAutocomplete = (path) => {
    const last = path.split('/').pop();
    setQuery(last);
    setAutocomplete([]);
    handleSearch(last);
  };

  const fetchGroupEvents = async (searchQuery = '') => {
    setEventsLoading(true);
    try {
      const url = searchQuery
        ? `${API_URL}/api/search?q=${encodeURIComponent(searchQuery)}&group_sessions=true`
        : `${API_URL}/api/calls/group-events`;
      const res = await fetch(url, searchQuery ? { headers } : {});
      if (res.ok) {
        const data = await res.json();
        setGroupEvents(data.events || data);
      }
    } catch {}
    setEventsLoading(false);
  };

  const joinEvent = async (eventId) => {
    try {
      const res = await fetch(`${API_URL}/api/calls/group-events/${eventId}/join`, { method: 'POST', headers });
      if (res.ok) { fetchGroupEvents(); }
    } catch {}
  };

  const formatTo12h = (t) => { const h = parseInt(t?.split(':')[0] || 0); if (h === 0) return '12:00 AM'; if (h < 12) return `${h}:${t?.split(':')[1] || '00'} AM`; if (h === 12) return `12:${t?.split(':')[1] || '00'} PM`; return `${h - 12}:${t?.split(':')[1] || '00'} PM`; };

  const ScoreBar = ({ label, value, color }) => (
    <div className="flex items-center space-x-2">
      <span className="text-[9px] text-zinc-500 w-16 shrink-0">{label}</span>
      <div className="flex-1 h-1 bg-zinc-800 rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${color}`} style={{ width: `${value}%` }} />
      </div>
      <span className="text-[9px] text-zinc-500 w-6 text-right">{Math.round(value)}</span>
    </div>
  );

  const ProfileCard = ({ u, showFeedback }) => (
    <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 hover:border-zinc-700 transition-all cursor-pointer" onClick={() => onProfileClick?.(u.user_id)} data-testid={`result-${u.user_id}`}>
      <div className="flex items-center space-x-3 mb-2">
        <div className={`w-11 h-11 rounded-full shrink-0 p-[2px] ${
          (u.expertise_tree || []).some(b => b.peer_verified) ? 'bg-gradient-to-br from-yellow-400 to-orange-500 shadow-[0_0_12px_rgba(251,191,36,0.4)]' :
          (u.expertise_tree || []).some(b => b.verified) ? 'bg-gradient-to-br from-yellow-400 to-orange-500 shadow-[0_0_8px_rgba(251,191,36,0.2)]' :
          'bg-gradient-to-br from-purple-500 to-orange-400'}`}>
          <div className="w-full h-full rounded-full bg-zinc-900 flex items-center justify-center overflow-hidden">
            {u.picture ? <img src={u.picture} alt="" className="w-full h-full object-cover" /> :
              <span className="text-sm font-bold text-white">{u.name?.[0]}</span>}
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center space-x-1.5">
            <p className="font-semibold text-sm truncate text-white">{u.name}</p>
            {(u.expertise_tree || []).some(b => b.peer_verified) && <Shield className="w-3.5 h-3.5 text-yellow-400 shrink-0" />}
          </div>
          {u.headline && <p className="text-[10px] text-zinc-500 truncate">{u.headline}</p>}
        </div>
        {u.match_score > 0 && (
          <div className={`text-xs font-bold px-2.5 py-1 rounded-full shrink-0 ${u.match_score >= 50 ? 'bg-green-500/15 text-green-400' : u.match_score >= 25 ? 'bg-yellow-500/15 text-yellow-400' : 'bg-zinc-800 text-zinc-400'}`}>
            {Math.round(u.match_score)}
          </div>
        )}
      </div>

      {/* Match reason */}
      {u.match_reason && <p className="text-xs text-zinc-400 mb-2">{u.match_reason}</p>}

      {/* Score breakdown — 5 signals */}
      {u.score_breakdown && (
        <div className="space-y-1 mb-2">
          <ScoreBar label="Expertise" value={u.score_breakdown.expertise_match} color="bg-purple-500" />
          <ScoreBar label="Reputation" value={u.score_breakdown.reputation} color="bg-yellow-500" />
          <ScoreBar label="Activity" value={u.score_breakdown.activity} color="bg-green-500" />
          <ScoreBar label="Response" value={u.score_breakdown.response_rate} color="bg-blue-500" />
          <ScoreBar label="Demand" value={u.score_breakdown.demand || 0} color="bg-orange-500" />
          <ScoreBar label="Freshness" value={u.score_breakdown.freshness || 100} color="bg-cyan-500" />
        </div>
      )}

      {/* Discovery-specific info */}
      {u.discover_reason && (
        <div className="flex items-center space-x-1.5 mb-2">
          <span className={`text-[10px] px-2 py-0.5 rounded-full ${
            u.discover_type === 'taxonomy_neighbor' ? 'bg-purple-500/10 text-purple-400' :
            u.discover_type === 'connection_graph' ? 'bg-blue-500/10 text-blue-400' :
            u.discover_type === 'resonance' ? 'bg-orange-500/10 text-orange-400' :
            'bg-green-500/10 text-green-400'}`}>
            {u.discover_type === 'taxonomy_neighbor' ? 'Similar Field' :
             u.discover_type === 'connection_graph' ? 'Network' :
             u.discover_type === 'resonance' ? 'Similar Taste' : 'Rising'}
          </span>
          <span className="text-[10px] text-zinc-500">{u.discover_reason}</span>
        </div>
      )}
      {/* Demand signal badge */}
      {u.demand_signal > 30 && (
        <div className="flex items-center space-x-1 mb-2">
          <TrendingUp className="w-3 h-3 text-orange-400" />
          <span className="text-[10px] text-orange-400 font-medium">High demand</span>
        </div>
      )}
      {u.new_skills?.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-2">
          {u.new_skills.map((s, i) => <span key={i} className="text-[10px] bg-purple-500/10 text-purple-400 px-2 py-0.5 rounded-full">+ {s}</span>)}
        </div>
      )}

      {/* Expertise tags */}
      {(u.expertise_tree || []).length > 0 && (
        <div className="flex flex-wrap gap-1">
          {u.expertise_tree.slice(0, 4).map((b, i) => (
            <span key={i} className={`text-[10px] px-2 py-0.5 rounded-full ${b.verified ? 'bg-yellow-500/10 text-yellow-400' : 'bg-zinc-800 text-zinc-400'}`}>
              {b.skill_level === 'expert' ? 'Expert' : b.skill_level === 'beginner' ? 'Beginner' : ''} {b.expertise || b.industry}
            </span>
          ))}
        </div>
      )}

      {/* Feedback */}
      {showFeedback && (
        <div className="flex items-center justify-end space-x-2 mt-2 pt-2 border-t border-zinc-800">
          <span className="text-[10px] text-zinc-600">Helpful?</span>
          <button onClick={() => handleFeedback(u.user_id, true)} className="text-zinc-600 hover:text-green-400 transition-colors" data-testid={`feedback-up-${u.user_id}`}><ThumbsUp className="w-3.5 h-3.5" /></button>
          <button onClick={() => handleFeedback(u.user_id, false)} className="text-zinc-600 hover:text-red-400 transition-colors" data-testid={`feedback-down-${u.user_id}`}><ThumbsDown className="w-3.5 h-3.5" /></button>
        </div>
      )}
    </div>
  );

  return (
    <div className="h-full overflow-y-auto bg-black" data-testid="search-tab">
      {/* Search Bar */}
      <div className="sticky top-0 z-20 bg-black/90 backdrop-blur-sm border-b border-zinc-800 p-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
          <input type="text" value={query} onChange={e => handleSearch(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && query.trim()) handleIntentSearch(); }}
            placeholder="Describe what you want to learn..."
            className="w-full bg-zinc-900 border border-zinc-800 rounded-xl pl-10 pr-24 py-3 text-sm outline-none focus:border-zinc-600 transition-colors"
            data-testid="search-input" />
          {query.trim() && (
            <button onClick={handleIntentSearch} disabled={intentLoading}
              className="absolute right-2 top-1/2 -translate-y-1/2 bg-white text-black text-xs font-bold px-3 py-1.5 rounded-lg flex items-center space-x-1 hover:bg-zinc-200 transition-colors"
              data-testid="intent-search-btn">
              <Zap className="w-3 h-3" /><span>{intentLoading ? '...' : 'Find Expert'}</span>
            </button>
          )}
        </div>

        {/* Autocomplete dropdown */}
        {autocomplete.length > 0 && (
          <div className="absolute left-4 right-4 bg-zinc-900 border border-zinc-800 rounded-xl mt-1 z-30 max-h-48 overflow-y-auto" data-testid="autocomplete">
            {autocomplete.map((path, i) => (
              <button key={i} onClick={() => selectAutocomplete(path)}
                className="w-full px-4 py-2 text-left text-sm hover:bg-zinc-800 transition-colors border-b border-zinc-800/50 last:border-0">
                <span className="text-zinc-400">{path.split('/').slice(0, -1).join(' / ')}{path.includes('/') ? ' / ' : ''}</span>
                <span className="text-white font-medium">{path.split('/').pop()}</span>
              </button>
            ))}
          </div>
        )}

        {/* Mode tabs + Filter toggle */}
        <div className="flex items-center space-x-1 mt-3">
          {[
            { id: 'search', label: 'Search', icon: Search },
            { id: 'intent', label: 'Find Expert', icon: Zap },
            { id: 'discover', label: 'Discover', icon: Compass },
            { id: 'sessions', label: 'Sessions', icon: Users },
          ].map(m => (
            <button key={m.id} onClick={() => { setActiveMode(m.id); if (m.id === 'discover') fetchDiscovery(); if (m.id === 'sessions') fetchGroupEvents(query); }}
              className={`flex items-center space-x-1 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${activeMode === m.id ? 'bg-zinc-800 text-white' : 'text-zinc-500'}`}
              data-testid={`mode-${m.id}`}>
              <m.icon className="w-3 h-3" /><span>{m.label}</span>
            </button>
          ))}
          <button onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center space-x-1 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ml-auto ${showFilters || activeFilterCount ? 'bg-purple-600 text-white' : 'text-zinc-500'}`}
            data-testid="filter-toggle">
            <Filter className="w-3 h-3" /><span>Filters{activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}</span>
          </button>
        </div>

        {/* Advanced Filter Panel */}
        {showFilters && (
          <div className="mt-3 p-3 bg-zinc-900/80 border border-zinc-800 rounded-xl space-y-2" data-testid="filter-panel">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-bold text-white">Advanced Filters</span>
              <button onClick={clearFilters} className="text-[10px] text-zinc-400 hover:text-white">Clear all</button>
            </div>
            {/* Filter pills */}
            <div className="flex flex-wrap gap-2">
              <button onClick={() => setFilters(p => ({ ...p, is_live: !p.is_live }))}
                className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-medium border transition-all ${filters.is_live ? 'bg-green-500/15 text-green-400 border-green-500/30' : 'bg-zinc-800 text-zinc-400 border-zinc-700'}`}
                data-testid="filter-live">
                <Radio className="w-3 h-3" /> Live Now
              </button>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {[
                { key: 'skill', placeholder: 'Skill / Expertise', icon: Zap, field: 'skill' },
                { key: 'city', placeholder: 'City / Location', icon: MapPin, field: 'city' },
                { key: 'company', placeholder: 'Company', icon: Briefcase, field: 'company' },
                { key: 'position', placeholder: 'Role / Position', icon: Briefcase, field: 'position' },
                { key: 'college', placeholder: 'College / University', icon: GraduationCap, field: 'college' },
              ].map(f => (
                <TypeaheadFilterInput key={f.key} value={filters[f.key]} onChange={v => setFilters(p => ({ ...p, [f.key]: v }))}
                  onSubmit={applyFilters} field={f.field} placeholder={f.placeholder} icon={f.icon} testId={`filter-${f.key}`} />
              ))}
            </div>
            <button onClick={applyFilters} className="w-full py-1.5 bg-white text-black text-xs font-bold rounded-lg hover:bg-zinc-200 transition-colors" data-testid="apply-filters">
              Apply Filters
            </button>
          </div>
        )}
      </div>

      <div className="p-4 space-y-3">
        {/* Search / Intent Results with Sections */}
        {(activeMode === 'search' || activeMode === 'intent') && (
          <>
            {(loading || intentLoading) && <div className="text-center py-8"><div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin mx-auto" /></div>}

            {/* Sectioned results when searching */}
            {query && !loading && sections.live_now?.length > 0 && (
              <div className="mb-4" data-testid="section-live">
                <div className="flex items-center gap-2 mb-2">
                  <Radio className="w-3.5 h-3.5 text-green-400" />
                  <h3 className="text-xs font-bold text-green-400 uppercase tracking-wider">Live Now</h3>
                </div>
                <div className="flex gap-3 overflow-x-auto pb-2 no-scrollbar">
                  {sections.live_now.map(u => (
                    <div key={u.user_id} className="shrink-0 w-40 bg-zinc-900 border border-green-500/20 rounded-xl p-3 cursor-pointer hover:border-green-500/40"
                      onClick={() => onProfileClick?.(u.user_id)} data-testid={`live-${u.user_id}`}>
                      <div className="flex items-center gap-2 mb-1">
                        <div className="w-8 h-8 rounded-full bg-zinc-800 overflow-hidden">
                          {u.picture ? <img src={u.picture} alt="" className="w-full h-full object-cover" /> : <span className="w-full h-full flex items-center justify-center text-xs font-bold">{u.name?.[0]}</span>}
                        </div>
                        <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
                      </div>
                      <p className="text-xs font-semibold truncate">{u.name}</p>
                      <p className="text-[10px] text-zinc-500 truncate">{u.headline || ''}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Top Rated section */}
            {query && !loading && sections.top_rated?.length > 0 && (
              <div className="mb-4" data-testid="section-top-rated">
                <h3 className="text-xs font-bold text-yellow-400 uppercase tracking-wider mb-2 flex items-center gap-1">
                  <TrendingUp className="w-3.5 h-3.5" /> Top Rated
                </h3>
                <div className="flex gap-3 overflow-x-auto pb-2 no-scrollbar">
                  {sections.top_rated.map(u => (
                    <div key={u.user_id} className="shrink-0 w-40 bg-zinc-900 border border-yellow-500/10 rounded-xl p-3 cursor-pointer hover:border-yellow-500/30"
                      onClick={() => onProfileClick?.(u.user_id)}>
                      <div className="w-8 h-8 rounded-full bg-zinc-800 overflow-hidden mb-1">
                        {u.picture ? <img src={u.picture} alt="" className="w-full h-full object-cover" /> : <span className="w-full h-full flex items-center justify-center text-xs font-bold">{u.name?.[0]}</span>}
                      </div>
                      <p className="text-xs font-semibold truncate">{u.name}</p>
                      {u.rating > 0 && <p className="text-[10px] text-yellow-400">{u.rating.toFixed(1)} rating</p>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Main results */}
            {results.map(u => <ProfileCard key={u.user_id} u={u} showFeedback={activeMode === 'intent'} />)}

            {/* Related posts */}
            {query && !loading && sections.posts?.length > 0 && (
              <div className="mt-4" data-testid="section-posts">
                <h3 className="text-xs font-bold text-zinc-400 uppercase tracking-wider mb-2">Related Posts</h3>
                {sections.posts.map((p, i) => (
                  <div key={p.post_id || i} className="bg-zinc-900 border border-zinc-800 rounded-xl p-3 mb-2">
                    <p className="text-xs text-zinc-400 font-medium mb-1">{p.user_name}</p>
                    <p className="text-sm text-white line-clamp-3">{p.content}</p>
                    <div className="flex items-center gap-3 mt-2 text-[10px] text-zinc-500">
                      <span>{p.likes_count || 0} likes</span>
                      <span>{p.comments_count || 0} comments</span>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {!loading && !intentLoading && results.length === 0 && query && (
              <p className="text-center text-zinc-500 py-8 text-sm">No results for "{query}"</p>
            )}

            {/* Empty state — discovery sections */}
            {!query && activeMode === 'search' && !activeFilterCount && (
              <div>
                {trendingSkills.length > 0 && (
                  <div className="mb-6" data-testid="trending-skills">
                    <h3 className="text-xs font-bold text-zinc-400 uppercase tracking-wider mb-2">Trending Skills</h3>
                    <div className="flex flex-wrap gap-2">
                      {trendingSkills.map((s, i) => (
                        <button key={i} onClick={() => { setFilters(p => ({ ...p, skill: s })); handleSearch(s); }}
                          className="px-3 py-1.5 bg-zinc-900 border border-zinc-800 rounded-full text-xs text-white hover:border-zinc-600 transition-colors">
                          {s}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {newUsers.length > 0 && (
                  <div className="mb-6" data-testid="new-users">
                    <h3 className="text-xs font-bold text-zinc-400 uppercase tracking-wider mb-2">New on Connect</h3>
                    {newUsers.map(u => <ProfileCard key={u.user_id} u={u} showFeedback={false} />)}
                  </div>
                )}
                {!trendingSkills.length && !newUsers.length && (
                  <div className="text-center py-12">
                    <Search className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
                    <p className="text-zinc-500 text-sm">Search by name, skill, or describe your problem</p>
                    <p className="text-zinc-600 text-xs mt-1">Use filters for precise results</p>
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {/* Discovery Engine */}
        {activeMode === 'discover' && (
          <>
            {/* Layer summary */}
            {discovery.layers && (
              <div className="flex items-center space-x-3 mb-3 flex-wrap gap-y-2">
                {discovery.layers.taxonomy_neighbors > 0 && (
                  <span className="text-[10px] bg-purple-500/10 text-purple-400 px-2 py-1 rounded-full flex items-center space-x-1">
                    <Compass className="w-3 h-3" /><span>{discovery.layers.taxonomy_neighbors} Similar Field</span>
                  </span>
                )}
                {discovery.layers.connection_graph > 0 && (
                  <span className="text-[10px] bg-blue-500/10 text-blue-400 px-2 py-1 rounded-full flex items-center space-x-1">
                    <Users className="w-3 h-3" /><span>{discovery.layers.connection_graph} Network</span>
                  </span>
                )}
                {discovery.layers.rising > 0 && (
                  <span className="text-[10px] bg-green-500/10 text-green-400 px-2 py-1 rounded-full flex items-center space-x-1">
                    <TrendingUp className="w-3 h-3" /><span>{discovery.layers.rising} Rising</span>
                  </span>
                )}
                {discovery.layers.resonance > 0 && (
                  <span className="text-[10px] bg-orange-500/10 text-orange-400 px-2 py-1 rounded-full flex items-center space-x-1">
                    <Zap className="w-3 h-3" /><span>{discovery.layers.resonance} Similar Taste</span>
                  </span>
                )}
              </div>
            )}
            {(discovery.suggestions || []).length === 0 ? (
              <div className="text-center py-12">
                <Compass className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
                <p className="text-zinc-500 text-sm">Add expertise to your profile to get discovery suggestions</p>
              </div>
            ) : (
              (discovery.suggestions || []).map(u => <ProfileCard key={u.user_id} u={u} showFeedback={false} />)
            )}
          </>
        )}

        {/* Group Sessions Discovery */}
        {activeMode === 'sessions' && (
          <div data-testid="group-sessions-discover">
            <h3 className="text-sm font-bold text-white mb-3">Discover Group Sessions</h3>
            {eventsLoading ? (
              <div className="text-center py-8"><div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin mx-auto" /></div>
            ) : groupEvents.length === 0 ? (
              <div className="text-center py-12">
                <Users className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
                <p className="text-zinc-500 text-sm">No upcoming group sessions</p>
                <p className="text-zinc-600 text-xs mt-1">Check back later or search by topic</p>
              </div>
            ) : (
              <div className="space-y-3">
                {groupEvents.map(evt => {
                  const attendeeList = evt.attendees || evt.participants || [];
                  const isJoined = attendeeList.some(a => (typeof a === 'string' ? a : a.user_id) === user?.user_id);
                  const spotsLeft = evt.max_participants - attendeeList.length;
                  return (
                    <div key={evt.event_id} className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 hover:border-zinc-700 transition-all" data-testid={`session-${evt.event_id}`}>
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1">
                          <p className="text-sm font-bold text-white">{evt.title}</p>
                          {evt.description && <p className="text-[11px] text-zinc-400 mt-0.5 line-clamp-2">{evt.description}</p>}
                        </div>
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold shrink-0 ml-2 ${evt.price > 0 ? 'bg-emerald-500/15 text-emerald-400' : 'bg-zinc-700 text-zinc-300'}`}>
                          {evt.price > 0 ? `₹${evt.price}` : 'Free'}
                        </span>
                      </div>
                      <div className="flex items-center gap-3 text-[10px] text-zinc-500 mb-2">
                        <span>{evt.date}</span>
                        <span>{formatTo12h(evt.time)}</span>
                        <span>{evt.duration_minutes}min</span>
                      </div>
                      <div className="flex items-center gap-2 mb-2 cursor-pointer" onClick={() => onProfileClick?.(evt.host_id)}>
                        <div className="w-6 h-6 rounded-full bg-zinc-800 flex items-center justify-center overflow-hidden text-[9px] font-bold">
                          {evt.host_picture ? <img src={`${API_URL}${evt.host_picture}`} alt="" className="w-full h-full object-cover" /> : evt.host_name?.[0]}
                        </div>
                        <span className="text-[11px] text-zinc-300 font-medium hover:text-purple-400">{evt.host_name}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-[10px] text-zinc-500">{attendeeList.length}/{evt.max_participants} joined</span>
                        {!isJoined && spotsLeft > 0 ? (
                          <button onClick={() => joinEvent(evt.event_id)}
                            className="px-4 py-1.5 bg-purple-600 hover:bg-purple-500 text-white text-xs font-bold rounded-lg transition-colors"
                            data-testid={`join-session-${evt.event_id}`}>
                            {evt.price > 0 ? `Join ₹${evt.price}` : 'Join Free'}
                          </button>
                        ) : isJoined ? (
                          <span className="text-[10px] text-emerald-400 font-semibold bg-emerald-500/10 px-2 py-1 rounded-full">Registered</span>
                        ) : (
                          <span className="text-[10px] text-red-400">Full</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}


function TypeaheadFilterInput({ value, onChange, onSubmit, field, placeholder, icon: Icon, testId }) {
  const [suggestions, setSuggestions] = useState([]);
  const [show, setShow] = useState(false);
  const debounceRef = useRef(null);

  const fetchSuggestions = async (q) => {
    if (!q || q.length < 2) { setSuggestions([]); setShow(false); return; }
    try {
      const res = await fetch(`${API_URL}/api/autocomplete/fields?field=${encodeURIComponent(field)}&q=${encodeURIComponent(q)}&limit=6`);
      if (res.ok) { const d = await res.json(); const s = d.suggestions || []; setSuggestions(s); setShow(s.length > 0); }
    } catch {}
  };

  const handleChange = (e) => {
    const v = e.target.value;
    onChange(v);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSuggestions(v), 200);
  };

  return (
    <div className="relative">
      <Icon className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-zinc-500 z-10" />
      <input type="text" placeholder={placeholder} value={value} onChange={handleChange}
        onFocus={() => { if (suggestions.length > 0) setShow(true); }}
        onBlur={() => setTimeout(() => setShow(false), 200)}
        onKeyDown={e => { if (e.key === 'Enter') { setShow(false); onSubmit?.(); } }}
        className="w-full bg-zinc-800 border border-zinc-700 rounded-lg py-1.5 pl-7 pr-2 text-[11px] text-white placeholder-zinc-500 focus:outline-none focus:border-zinc-600"
        data-testid={testId} />
      {show && suggestions.length > 0 && (
        <div className="absolute left-0 right-0 top-full z-50 bg-zinc-800 border border-zinc-700 rounded-lg mt-0.5 max-h-32 overflow-y-auto shadow-xl" data-testid={`${testId}-suggestions`}>
          {suggestions.map((s, i) => (
            <button key={i} onMouseDown={() => { onChange(s); setShow(false); setSuggestions([]); }}
              className="w-full text-left px-3 py-1.5 text-[11px] text-zinc-300 hover:bg-zinc-700 hover:text-white transition-colors border-b border-zinc-700/30 last:border-0">
              {s}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
